import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Save, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Application } from '@/types/database.types';

interface DependentRecord {
  id?: string;
  employee_census_id: string;
  first_name: string;
  last_name: string;
  middle_initial?: string;
  ssn?: string;
  date_of_birth?: string;
  gender?: string;
  relationship?: string;
  student_status?: string;
  disabled?: boolean;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  employee_id: string;
}

interface DependentCensusBuilderProps {
  applicationId?: string;
  onSave?: (data: Partial<Application>) => void;
}

export const DependentCensusBuilder = ({ applicationId, onSave }: DependentCensusBuilderProps) => {
  const [dependents, setDependents] = useState<DependentRecord[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSavingProgress, setIsSavingProgress] = useState(false);
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<DependentRecord>();

  const loadData = async () => {
    if (!applicationId) return;
    
    setIsLoading(true);
    try {
      // Load employees
      const { data: employeeData, error: employeeError } = await supabase
        .from('employee_census')
        .select('id, first_name, last_name, employee_id')
        .eq('application_id', applicationId)
        .order('created_at', { ascending: true });

      if (employeeError) throw employeeError;
      setEmployees(employeeData || []);

      // Load dependents
      const { data: dependentData, error: dependentError } = await supabase
        .from('dependent_census')
        .select('*')
        .eq('application_id', applicationId)
        .order('created_at', { ascending: true });

      if (dependentError) throw dependentError;
      setDependents(dependentData || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load dependent data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [applicationId]);

  const onSubmit = async (data: DependentRecord) => {
    if (!applicationId) return;

    try {
      const { error } = await supabase
        .from('dependent_census')
        .insert([{
          ...data,
          application_id: applicationId,
          user_id: (await supabase.auth.getUser()).data.user?.id,
        }]);

      if (error) throw error;
      
      toast.success('Dependent added successfully');
      reset();
      loadData();
    } catch (error) {
      console.error('Error adding dependent:', error);
      toast.error('Failed to add dependent');
    }
  };

  const deleteDependent = async (id: string) => {
    try {
      const { error } = await supabase
        .from('dependent_census')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Dependent deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting dependent:', error);
      toast.error('Failed to delete dependent');
    }
  };

  const exportToCsv = () => {
    if (dependents.length === 0) {
      toast.error('No dependent data to export');
      return;
    }

    const headers = [
      'Employee ID', 'Employee Name', 'Dependent First Name', 'Dependent Last Name',
      'Middle Initial', 'SSN', 'Date of Birth', 'Gender', 'Relationship',
      'Student Status', 'Disabled'
    ];

    const csvContent = [
      headers.join(','),
      ...dependents.map(dep => {
        const employee = employees.find(emp => emp.id === dep.employee_census_id);
        return [
          employee?.employee_id || '',
          employee ? `${employee.first_name} ${employee.last_name}` : '',
          dep.first_name || '',
          dep.last_name || '',
          dep.middle_initial || '',
          dep.ssn || '',
          dep.date_of_birth || '',
          dep.gender || '',
          dep.relationship || '',
          dep.student_status || '',
          dep.disabled ? 'Yes' : 'No'
        ].map(field => `"${field}"`).join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dependent-census-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success('Dependent census exported successfully');
  };

  const saveProgress = async () => {
    if (!onSave) return;
    
    setIsSavingProgress(true);
    try {
      // Just save a simple update to track that user has worked on this section
      const progressUpdate = {
        last_modified: new Date().toISOString()
      };
      
      onSave(progressUpdate);
      toast.success('Dependent census progress saved');
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error('Failed to save progress');
    } finally {
      setIsSavingProgress(false);
    }
  };

  const getEmployeeName = (employeeId: string) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.first_name} ${employee.last_name} (${employee.employee_id})` : 'Unknown Employee';
  };

  return (
    <div className="space-y-6">
      {/* Add Dependent Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Dependent
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="employee_census_id">Employee</Label>
              <Select onValueChange={(value) => setValue('employee_census_id', value)} disabled={employees.length === 0}>
                <SelectTrigger>
                  <SelectValue placeholder={employees.length === 0 ? "No employees available - add employees first" : "Select employee"} />
                </SelectTrigger>
                {employees.length > 0 && (
                  <SelectContent>
                    {employees.map((employee) => (
                      <SelectItem key={employee.id} value={employee.id}>
                        {employee.first_name} {employee.last_name} ({employee.employee_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                )}
              </Select>
              {employees.length === 0 && (
                <p className="text-sm text-muted-foreground mt-1">
                  Note: Add employees in the Employee Census tab to link dependents to specific employees.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  {...register('first_name', { required: 'First name is required' })}
                  placeholder="Jane"
                />
                {errors.first_name && (
                  <p className="text-sm text-red-600 mt-1">{errors.first_name.message}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  {...register('last_name', { required: 'Last name is required' })}
                  placeholder="Doe"
                />
                {errors.last_name && (
                  <p className="text-sm text-red-600 mt-1">{errors.last_name.message}</p>
                )}
              </div>
              
              <div>
                <Label htmlFor="middle_initial">Middle Initial</Label>
                <Input
                  id="middle_initial"
                  {...register('middle_initial')}
                  placeholder="M"
                  maxLength={1}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="date_of_birth">Date of Birth</Label>
                <Input
                  id="date_of_birth"
                  type="date"
                  {...register('date_of_birth')}
                />
              </div>
              
              <div>
                <Label htmlFor="gender">Gender</Label>
                <Select onValueChange={(value) => setValue('gender', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="relationship">Relationship</Label>
                <Select onValueChange={(value) => setValue('relationship', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Spouse">Spouse</SelectItem>
                    <SelectItem value="Child">Child</SelectItem>
                    <SelectItem value="Stepchild">Stepchild</SelectItem>
                    <SelectItem value="Domestic Partner">Domestic Partner</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="student_status">Student Status</Label>
                <Select onValueChange={(value) => setValue('student_status', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not a student">Not a student</SelectItem>
                    <SelectItem value="Full-time student">Full-time student</SelectItem>
                    <SelectItem value="Part-time student">Part-time student</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="submit" className="flex-1">
                <Save className="h-4 w-4 mr-2" />
                Add Dependent
              </Button>
              {onSave && (
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={saveProgress}
                  disabled={isSavingProgress}
                  className="flex-1"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {isSavingProgress ? 'Saving...' : 'Save Progress'}
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Dependent List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Dependent List ({dependents.length})</CardTitle>
            {dependents.length > 0 && (
              <Button onClick={exportToCsv} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center text-muted-foreground">Loading dependents...</p>
          ) : dependents.length === 0 ? (
            <p className="text-center text-muted-foreground">No dependents added yet</p>
          ) : (
            <div className="space-y-3">
              {dependents.map((dependent) => (
                <div
                  key={dependent.id}
                  className="flex items-center justify-between p-4 border rounded-lg bg-card"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="font-medium">
                          {dependent.first_name} {dependent.last_name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {dependent.relationship} of {getEmployeeName(dependent.employee_census_id)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          DOB: {dependent.date_of_birth || 'Not provided'} | 
                          Student: {dependent.student_status || 'Not specified'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => deleteDependent(dependent.id!)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};